import React, { FC, useEffect, useState } from 'react';
import * as yup from 'yup';
import { Button, Modal } from 'react-bootstrap';

import BaseModal from '../../../components/BaseModal/BaseModal';
import Forms from '../../../components/Forms/Forms';
import { useClickThruLabel } from '../../../hooks/useClickThruLabel';
import { OrderQuoteData } from '../../../services/digitalCodesService';
import { getSanitizedHTML } from '../../../utils/stringUtils';
import { validateToSchema } from '../../../utils/validationUtils';
import { isEmptyObject } from '../../../utils/dataUtils';

import './SubmitDCOrderConfirmationModal.css';

const ACKNOWLEDGE_CHECKBOX_FIELD = 'acknowledgement-checkbox';

interface SubmitDCOrderConfirmationModalProps {
	show: boolean;
	isSubmitting: boolean;
	onCancel: () => void;
	onConfirm: (formValues: Record<string, any>) => void;
	clickThruTemplate?: OrderQuoteData['click_thru_template'];
}

const buildSchema = (sections: NonNullable<OrderQuoteData['click_thru_template']>['Sections']) => {
	const shape: Record<string, yup.AnySchema> = {};
	sections
		.filter((s) => s.type === ACKNOWLEDGE_CHECKBOX_FIELD)
		.forEach((s) => {
			shape[s.id] = yup
				.boolean()
				.default(false)
				.required('Box must be checked')
				.test('must_be_true', 'Box must be checked', (value) => value === true);
		});
	return yup.object().shape(shape);
};

const SubmitDCOrderConfirmationModal: FC<SubmitDCOrderConfirmationModalProps> = ({
	show,
	isSubmitting,
	onCancel,
	onConfirm,
	clickThruTemplate,
}) => {
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [showAllErrors, setShowAllErrors] = useState(false);

	const { renderLabelWithLink, notFoundMessage, clearNotFoundMessage } =
		useClickThruLabel(clickThruTemplate);

	// Reset form state when modal opens/closes
	useEffect(() => {
		if (show) {
			setFormValues({});
			setShowAllErrors(false);
			clearNotFoundMessage();
		}
	}, [show]);

	const schema = clickThruTemplate ? buildSchema(clickThruTemplate.Sections) : null;
	const validationErrors = schema ? validateToSchema(schema, formValues) : {};

	const handleConfirm = () => {
		if (schema && !isEmptyObject(validationErrors)) {
			setShowAllErrors(true);
			return;
		}
		onConfirm(formValues);
	};

	return (
		<>
			<BaseModal show={show} isSubmitting={isSubmitting} onCancel={onCancel} size="lg">
				<BaseModal.Title>Confirm order</BaseModal.Title>
				<p>
					The price reflected in your Order Detail is an estimate of the total cost of your
					order.
				</p>
				<p>
					A pro-forma invoice will be available to download after your order is approved by
					Nintendo. Your pro-forma for this order will reflect any price difference.
				</p>
				<p>
					<span style={{ backgroundColor: '#FFFF33' }}>
						Please include the PO number in your wire transfer. Note that you are
						responsible for all wire transfer fees.
					</span>{' '}
					The final amount transferred to Nintendo of America must match the amount shown on
					the pro-forma invoice.
				</p>
				<p>
					Once your order is accepted by NOA, it shall be governed by, and subject to, the
					terms and conditions of your Content and Distribution License Agreement for the
					"Platform" selected when placing the order.
				</p>
				<p>
					Please contact{' '}
					<a href="mailto:thirdpartypublisher@noa.nintendo.com">
						thirdpartypublisher@noa.nintendo.com
					</a>{' '}
					to change delivery or activation dates after the order is approved. Please confirm
					that you would like to proceed with this order by selecting Submit Order below.
				</p>
				{clickThruTemplate && (
					<Forms
						values={formValues}
						onChange={(newValues) => setFormValues(newValues)}
						validationErrors={validationErrors}
						showAllErrors={showAllErrors}
					>
						{clickThruTemplate.Sections.filter(
							(section) => section.type === ACKNOWLEDGE_CHECKBOX_FIELD,
						).map((section) => (
							<div key={section.id} className="SubmitDCOrderConfirmationModal__acknowledge-checkbox">
								<Forms.SingleCheckbox id={section.id} vertical>
									<Forms.Label>{renderLabelWithLink(section.label)}</Forms.Label>
								</Forms.SingleCheckbox>
							</div>
						))}
						<small
							className="float-right"
							dangerouslySetInnerHTML={{
								__html: getSanitizedHTML(`${clickThruTemplate.Version.label}: ${clickThruTemplate.Version.text}`),
							}}
						/>
					</Forms>
				)}
				<BaseModal.Submit variant="primary" onClick={handleConfirm}>
					Submit Order
				</BaseModal.Submit>
				<BaseModal.Cancel onClick={onCancel}>Cancel</BaseModal.Cancel>
			</BaseModal>

			<Modal show={!!notFoundMessage} onHide={clearNotFoundMessage} centered>
				<Modal.Header closeButton>
					<Modal.Title>Notice</Modal.Title>
				</Modal.Header>
				<Modal.Body>{notFoundMessage}</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={clearNotFoundMessage}>
						OK
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default SubmitDCOrderConfirmationModal;
